import React from 'react'
import ReactDOM from 'react-dom/client'
import App from '../App'

import type { LibConfig } from '../types/lib-config'
import { ShareButton } from '../components/share/ShareButton'
import { isColorTooLight } from '../helpers/setCustomThemeColor'

// UMD-only: SCSS -> string via rollup-plugin-postcss (extract:false, inject:false)
import scssMod from '../sass/app.scss'

const WIDGET_ID_PREFIX = 'osirusai-agent'
const DEFAULT_INSTANCE_ID = 'default'
const DEFAULT_RIGHT = 30
const DEFAULT_BOTTOM = 105
const STACK_STEP = 28

const isBrowser =
  typeof window !== 'undefined' && typeof document !== 'undefined'

type WidgetInstanceEntry = {
  root: ReactDOM.Root
  container: HTMLElement
  lastConfig: LibConfig
}

type OsirusRootsState = {
  widgets: Map<string, WidgetInstanceEntry>
  share: Map<Element, ReactDOM.Root>
}

declare global {
  interface Window {
    __osirusRoots?: OsirusRootsState
    // Optional global override set by host page
    __OSIRUS_CSS_URL?: string
  }
}

function ensureRoots() {
  if (!isBrowser) return { widgets: new Map(), share: new Map() }

  if (!window.__osirusRoots) {
    window.__osirusRoots = { widgets: new Map(), share: new Map() }
  } else {
    window.__osirusRoots.widgets = window.__osirusRoots.widgets ?? new Map()
    window.__osirusRoots.share = window.__osirusRoots.share ?? new Map()
  }
  return window.__osirusRoots
}

function normalizeInstanceId(raw: unknown): string {
  const text = typeof raw === 'string' ? raw.trim() : ''
  if (!text) return DEFAULT_INSTANCE_ID
  const safe = text.replace(/[^a-zA-Z0-9_-]/g, '-')
  return safe || DEFAULT_INSTANCE_ID
}

function buildWidgetContainerId(instanceId: string): string {
  return instanceId === DEFAULT_INSTANCE_ID
    ? WIDGET_ID_PREFIX
    : `${WIDGET_ID_PREFIX}-${instanceId}`
}

function resolveMountTarget(target: unknown): HTMLElement | null {
  if (!isBrowser) return null
  if (typeof target === 'string') {
    const selector = target.trim()
    if (!selector) return null
    return document.querySelector(selector) as HTMLElement | null
  }
  if (target instanceof HTMLElement) return target
  return null
}

function createOrGetWidgetContainer(instanceId: string, mountTo?: unknown): HTMLElement {
  const explicitMount = resolveMountTarget(mountTo)
  if (explicitMount) return explicitMount

  const widgetId = buildWidgetContainerId(instanceId)
  const existing = document.getElementById(widgetId) as HTMLElement | null
  if (existing) return existing

  const container = document.createElement('div')
  container.id = widgetId
  container.style.position = 'fixed'
  container.style.zIndex = '2147483647'
  container.style.left = '0'
  container.style.top = '0'
  document.body.appendChild(container)
  return container
}

function clearNode(node: Node) {
  while (node.firstChild) node.removeChild(node.firstChild)
}

function getScriptAttr(name: string): string | null {
  if (!isBrowser) return null
  const cur = document.currentScript as HTMLScriptElement | null
  const val = cur?.getAttribute(name)
  if (val) return val
  const any = document.querySelector(`script[${name}]`) as HTMLScriptElement | null
  return any?.getAttribute(name) ?? null
}

// ---------- CSS injection (UMD) ----------

// Default location where your pipeline uploads dist/umd
const DEFAULT_ASSET_BASE = 'https://agent.osirus.ai/lib/umd'

function ensureCssLink(shadowRoot: ShadowRoot, href: string) {
  if (shadowRoot.querySelector('[data-osirusai-css="1"]')) return
  const link = document.createElement('link')
  link.setAttribute('data-osirusai-css', '1')
  link.rel = 'stylesheet'
  link.href = href
  shadowRoot.appendChild(link)
}

function ensureCssStyleTag(shadowRoot: ShadowRoot, cssText: string) {
  if (shadowRoot.querySelector('[data-osirusai-css="1"]')) return
  const style = document.createElement('style')
  style.setAttribute('data-osirusai-css', '1')
  style.textContent = cssText
  shadowRoot.appendChild(style)
}

// Separate marker so bootstrap-icons link doesn't get blocked by widget CSS injection
function ensureBootstrapIconsLink(shadowRoot: ShadowRoot, href: string) {
  if (shadowRoot.querySelector('[data-osirusai-bi="1"]')) return
  const link = document.createElement('link')
  link.setAttribute('data-osirusai-bi', '1')
  link.rel = 'stylesheet'
  link.href = href
  shadowRoot.appendChild(link)
}

function normalizeCss(mod: unknown): string {
  if (typeof mod === 'string') return mod
  const d = (mod as any)?.default
  return typeof d === 'string' ? d : ''
}

function normalizeBase(url: string): string {
  return url.replace(/\/+$/, '')
}

function resolveAssetBase(config?: { assetBaseUrl?: string }) {
  // 1) explicit config override
  if (config?.assetBaseUrl) return normalizeBase(String(config.assetBaseUrl))

  // 2) global override (host page can set this)
  if (window.__OSIRUS_CSS_URL) return normalizeBase(String(window.__OSIRUS_CSS_URL))

  // 3) script attribute override (optional)
  const attr = getScriptAttr('data-osirus-asset-base') || getScriptAttr('data-asset-base')
  if (attr) return normalizeBase(String(attr))

  // 4) default
  return DEFAULT_ASSET_BASE
}

function insertStyleSheetIfNeeded(
  shadowRoot: ShadowRoot,
  config?: Partial<LibConfig> & {
    cssUrl?: string
    assetBaseUrl?: string
    biCssUrl?: string
  }
) {
  const assetBase = resolveAssetBase(config)

  // Always inject bootstrap icons INSIDE the shadow root
  const biHref = config?.biCssUrl
    ? String(config.biCssUrl)
    : `${assetBase}/bootstrap-icons.css`
  ensureBootstrapIconsLink(shadowRoot, biHref)

  // Inject widget CSS (inline when available)
  const cssText = normalizeCss(scssMod)
  if (cssText.trim()) {
    ensureCssStyleTag(shadowRoot, cssText)
    return
  }

  // Only if the caller explicitly passes cssUrl
  if (config?.cssUrl) ensureCssLink(shadowRoot, String(config.cssUrl))
}

function withDefaultPlacement(
  config: LibConfig,
  instanceId: string,
  instanceIndex: number
): LibConfig {
  if ((config as any)?.placement && typeof (config as any).placement === 'object') {
    return config
  }

  const stackIndex = instanceId === DEFAULT_INSTANCE_ID ? 0 : Math.max(0, instanceIndex)
  const offset = STACK_STEP * stackIndex

  return {
    ...(config as any),
    placement: {
      position: 'fixed',
      right: `${DEFAULT_RIGHT + offset}px`,
      bottom: `${DEFAULT_BOTTOM + offset}px`,
    },
  } as LibConfig
}

function getRootEntry(instanceId: string): WidgetInstanceEntry | undefined {
  const roots = ensureRoots()
  return roots.widgets.get(instanceId)
}

function removeWidgetInstance(instanceId: string) {
  const roots = ensureRoots()
  const entry = roots.widgets.get(instanceId)
  if (!entry) return

  try {
    const canUnmount = Boolean(
      entry.root &&
      entry.container &&
      ((entry.container as any).isConnected || entry.container.parentNode)
    )
    if (canUnmount) entry.root.unmount()
  } catch {
    // ignore
  }

  const explicitMount = resolveMountTarget((entry.lastConfig as any)?.mountTo)
  if (!explicitMount && entry.container) {
    try {
      entry.container.remove()
    } catch {
      // ignore
    }
  } else if (entry.container?.shadowRoot) {
    clearNode(entry.container.shadowRoot)
  } else if (entry.container) {
    clearNode(entry.container)
  }

  roots.widgets.delete(instanceId)
}

export function destroyImpl(instanceId?: string) {
  if (!isBrowser) return
  const roots = ensureRoots()
  const requestedInstanceId = normalizeInstanceId(instanceId)

  if (instanceId) {
    removeWidgetInstance(requestedInstanceId)
    return
  }

  for (const key of Array.from(roots.widgets.keys())) {
    removeWidgetInstance(key)
  }

  try {
    roots.share?.forEach((root, el) => {
      const host = el as HTMLElement
      const canUnmountShareRoot = Boolean(
        (host as any)?.isConnected || host.parentNode || host.childNodes.length
      )
      try {
        if (canUnmountShareRoot) root.unmount()
      } catch {
        // ignore
      }
      const sr = host.shadowRoot
      if (sr) clearNode(sr)
    })
  } catch {
    // ignore
  }

  roots.share?.clear()
}

export function updateImpl(patch: Partial<LibConfig>) {
  if (!isBrowser) return

  const roots = ensureRoots()
  const instanceId = normalizeInstanceId((patch as any)?.instanceId)
  const entry = roots.widgets.get(instanceId)

  if (!entry) {
    if ((patch as any)?.agentId || (patch as any)?.name) {
      initImpl(patch as LibConfig)
    }
    return
  }

  const next: LibConfig = {
    ...(entry.lastConfig as any),
    ...(patch as any),
    address: {
      ...((entry.lastConfig as any)?.address ?? {}),
      ...((patch as any)?.address ?? {}),
    },
  }

  initImpl(next)
}

// UMD: widget uses shadow root + injected CSS
export function initImpl(
  config: LibConfig & {
    cssUrl?: string
    assetBaseUrl?: string
    biCssUrl?: string
  }
) {
  if (!isBrowser) return

  const roots = ensureRoots()
  const instanceId = normalizeInstanceId((config as any)?.instanceId)
  const existing = getRootEntry(instanceId)
  const normalizedConfig = withDefaultPlacement(config, instanceId, roots.widgets.size)

  if (existing) {
    existing.lastConfig = normalizedConfig
    existing.root.render(
      <React.StrictMode>
        <App config={normalizedConfig} />
      </React.StrictMode>
    )
    setTimeout(() => initShareButtonsImpl(normalizedConfig), 250)
    return
  }

  const container = createOrGetWidgetContainer(instanceId, (normalizedConfig as any)?.mountTo)
  const shadowRoot = container.shadowRoot ?? container.attachShadow({ mode: 'open' })
  insertStyleSheetIfNeeded(shadowRoot, normalizedConfig)

  const mountId = `osirus-mount-${instanceId}`
  let mount = shadowRoot.querySelector(`#${mountId}`) as HTMLElement | null
  if (!mount) {
    mount = document.createElement('div')
    mount.id = mountId
    shadowRoot.appendChild(mount)
  }

  const root = ReactDOM.createRoot(mount)
  roots.widgets.set(instanceId, {
    root,
    container,
    lastConfig: normalizedConfig,
  })

  root.render(
    <React.StrictMode>
      <App config={normalizedConfig} />
    </React.StrictMode>
  )

  setTimeout(() => initShareButtonsImpl(normalizedConfig), 250)
}

// UMD: share buttons also shadowed + injected CSS
export function initShareButtonsImpl(
  config?: Partial<LibConfig> & {
    cssUrl?: string
    assetBaseUrl?: string
    biCssUrl?: string
  }
) {
  if (!isBrowser) return

  const roots = ensureRoots()
  const containers = document.querySelectorAll('.osirus-share-button')

  containers.forEach((container) => {
    const host = container as HTMLElement
    if (roots.share?.has(host)) return

    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
    insertStyleSheetIfNeeded(shadowRoot, config)

    const mount = document.createElement('div')
    shadowRoot.appendChild(mount)

    const root = ReactDOM.createRoot(mount)
    roots.share?.set(host, root)

    const {
      shareUrl,
      isFloating,
      top,
      bottom,
      left,
      right,
      backgroundColor,
      backgroundHoverColor,
      width,
      height,
      border,
      borderRadius,
    } = host.dataset

    if (backgroundColor) {
      host.style.setProperty(
        '--osirus-share-button-background-color',
        `#${backgroundColor}`
      )
      host.style.setProperty(
        '--osirus-share-button-icon-color',
        isColorTooLight(backgroundColor) ? '#000000' : '#ffffff'
      )
    }

    if (backgroundHoverColor) {
      host.style.setProperty(
        '--osirus-share-button-background-hover-color',
        `#${backgroundHoverColor}`
      )
    }

    if (width) host.style.setProperty('--osirus-share-button-width', width)
    if (height) host.style.setProperty('--osirus-share-button-height', height)
    if (border) host.style.setProperty('--osirus-share-button-border', border)
    if (borderRadius) {
      host.style.setProperty('--osirus-share-button-border-radius', borderRadius)
    }

    root.render(
      <React.StrictMode>
        <ShareButton
          shareUrl={shareUrl}
          isFloating={isFloating === 'true'}
          position={{ top, bottom, left, right }}
        />
      </React.StrictMode>
    )
  })
}

export default { initImpl, initShareButtonsImpl, destroyImpl, updateImpl }
